import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../../config/environment';
import DashShell from '../dash/DashShell';

interface VPSInstance {
  id: string;
  name: string;
  container_name: string;
  status: 'creating' | 'running' | 'stopped' | 'suspended' | 'terminated' | 'failed';
  ipv4_address: string | null;
  ipv6_address: string | null;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  plan_name: string;
  monthly_price: number;
  server_name: string;
  server_location: string;
  os_template: string;
  root_password?: string;
  ssh_keys: string[];
  created_at: string;
  updated_at: string;
}

interface VPSMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  memory_mb?: number;
  memory_total_mb?: number;
  disk_gb?: number;
  disk_total_gb?: number;
  network_in: number;
  network_out: number;
  uptime: number;
  // Proxmox doesn't return these; backend sends null and the UI renders --.
  load_average: number[] | null;
  processes: number | null;
  connections: number | null;
}

// Time-series point from Proxmox rrddata. Numbers are: cpu 0..1, mem/maxmem
// in bytes, netin/netout in bytes/sec. Other fields exist but we don't use them.
interface VPSMetricsPoint {
  time: number;
  cpu?: number;
  mem?: number;
  maxmem?: number;
  netin?: number;
  netout?: number;
}

/**
 * Minimal inline SVG sparkline. No deps, no axes — designed for an at-a-glance
 * trend panel next to the gauge. Scales y to the dataset's min/max, draws a
 * filled area + line. Returns an empty box for empty/all-zero series so the
 * card doesn't render a flat line at the bottom.
 */
const Sparkline: React.FC<{
  values: number[];
  color: string;       // stroke + fill base, e.g. '#3b82f6'
  height?: number;
  unit?: string;       // display alongside the last value, e.g. '%' or 'B/s'
  format?: (v: number) => string;
}> = ({ values, color, height = 60, unit = '', format }) => {
  const cleaned = values.filter(v => typeof v === 'number' && isFinite(v));
  if (cleaned.length < 2) {
    return <div className="text-xs text-gray-400 italic px-2">Collecting data…</div>;
  }
  const min = Math.min(...cleaned);
  const max = Math.max(...cleaned);
  const range = max - min || 1;
  const w = 200;
  const h = height;
  const step = w / (cleaned.length - 1);
  const points = cleaned.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${w},${h} L 0,${h} Z`;
  const last = cleaned[cleaned.length - 1];
  const lastFmt = format ? format(last) : last.toFixed(2);
  return (
    <div className="flex items-end gap-3">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="flex-1 h-16">
        <path d={areaPath} fill={color} fillOpacity={0.15} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="text-xs text-gray-600 whitespace-nowrap">
        <div className="font-medium text-gray-900">{lastFmt}{unit}</div>
        <div>min {format ? format(min) : min.toFixed(2)}{unit}</div>
        <div>max {format ? format(max) : max.toFixed(2)}{unit}</div>
      </div>
    </div>
  );
};

const EnhancedVPSInstanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'overview';
  
  const [vps, setVps] = useState<VPSInstance | null>(null);
  const [metrics, setMetrics] = useState<VPSMetrics | null>(null);
  const [series, setSeries] = useState<VPSMetricsPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'console' | 'networking' | 'snapshots' | 'settings' | 'monitoring'>(defaultTab as any);
  
  // Console state — `consoleUrl` is set to /vnc/<sessionId> once a session
  // is minted; the iframe renders only when this is present.
  const [consoleUrl, setConsoleUrl] = useState<string | null>(null);
  const [consoleStatus, setConsoleStatus] = useState<string>('');
  const [consoleError, setConsoleError] = useState<string>('');
  
  // Settings states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newSSHKey, setNewSSHKey] = useState('');
  const [sshKeyName, setSshKeyName] = useState('');

  // Power-action state. `busyAction` is the action currently in flight so
  // we can disable all power buttons + show a spinner until the upstream
  // qemu task completes AND the VPS status reflects the new state.
  // `targetStatus` is the state we expect post-action so the badge can
  // render "starting…" / "stopping…" while the backend catches up.
  const [busyAction, setBusyAction] = useState<null | 'start' | 'stop' | 'restart' | 'destroy'>(null);
  const [targetStatus, setTargetStatus] = useState<null | 'running' | 'stopped'>(null);

  useEffect(() => {
    if (id) {
      fetchVPSDetails();
      const interval = setInterval(fetchVPSMetrics, 30000); // Update metrics every 30 seconds
      return () => clearInterval(interval);
    }
  }, [id]);

  const fetchVPSDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/vps/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVps(data.data);
        await fetchVPSMetrics();
      } else {
        setError('VPS not found');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching VPS details:', error);
      setError('Failed to load VPS details');
      setLoading(false);
    }
  };

  const fetchVPSMetrics = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/vps/${id}/metrics`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      // Backend returns { success, data: { current, series, timeframe } }.
      const current = json?.data?.current;
      const s = json?.data?.series;
      if (current) setMetrics(current as VPSMetrics);
      if (Array.isArray(s)) setSeries(s);
    } catch (e) {
      // Soft fail — leave the previous metrics in place so the dashboard
      // doesn't flash to "--" on a transient network blip.
      console.error('Error fetching VPS metrics:', e);
    }
  };

  const handleVPSControl = async (action: 'start' | 'stop' | 'restart' | 'destroy') => {
    // Guard against double-clicks. The backend is idempotent (it reconciles
    // already-running/already-stopped states), but the customer experience
    // is better if we don't even let them spam the button.
    if (busyAction) return;

    if (action === 'destroy') {
      if (!window.confirm('Are you sure you want to destroy this VPS? This action cannot be undone and all data will be lost!')) {
        return;
      }
    }

    setError('');
    setSuccess('');
    setBusyAction(action);
    // The post-action steady state. For restart we end up running again.
    const target: 'running' | 'stopped' | null =
      action === 'start' || action === 'restart' ? 'running'
      : action === 'stop' ? 'stopped'
      : null;
    setTargetStatus(target);

    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'destroy'
        ? getApiUrl(`/vps/${id}`)
        : getApiUrl(`/vps/${id}/${action}`);

      const response = await fetch(endpoint, {
        method: action === 'destroy' ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();

      if (!result.success) {
        setError(result.error || `Failed to ${action} VPS`);
        setBusyAction(null);
        setTargetStatus(null);
        return;
      }

      setSuccess(`VPS ${action} initiated`);

      if (action === 'destroy') {
        // Don't sit on this page — go back to the list. The VPS row will be
        // gone (or marked terminated) within seconds.
        navigate('/customer/vps');
        return;
      }

      // Fast-poll the VPS endpoint until status reaches the target, or we
      // give up after ~45s. Without this, the user has to wait for the
      // ambient 30s metrics tick before they see the badge update. Backend
      // already syncs status from Proxmox so the truth catches up quickly.
      const deadline = Date.now() + 45_000;
      const POLL_MS = 1500;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise(r => setTimeout(r, POLL_MS));
        try {
          const r = await fetch(getApiUrl(`/vps/${id}`), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) {
            const j = await r.json();
            if (j?.data) setVps(j.data);
            if (j?.data?.status === target) break;
          }
        } catch { /* keep polling */ }
        if (Date.now() > deadline) break;
      }
      // Final refresh of metrics so the gauges reflect the new state.
      fetchVPSMetrics();
    } catch (error) {
      console.error(`Error ${action} VPS:`, error);
      setError(`Failed to ${action} VPS`);
    } finally {
      setBusyAction(null);
      setTargetStatus(null);
    }
  };

  // Mint a new noVNC session and render the viewer inline in an iframe.
  // The customer-facing /vnc/<sessionId> page handles the actual WebSocket +
  // RFB rendering; we just embed it.
  const connectConsole = async () => {
    if (!id) return;
    setConsoleError('');
    setConsoleStatus('Requesting console session...');
    setConsoleUrl(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(getApiUrl(`/vps/${id}/console`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await r.json();
      if (!r.ok || !data.success || !data.consoleUrl) {
        setConsoleError(data.error || 'Failed to start console session');
        setConsoleStatus('');
        return;
      }
      setConsoleStatus(`Session active (expires in ${data.expiresInSec}s)`);
      setConsoleUrl(data.consoleUrl);
    } catch (e: any) {
      setConsoleError(e?.message || String(e));
      setConsoleStatus('');
    }
  };

  const disconnectConsole = () => {
    setConsoleUrl(null);
    setConsoleStatus('');
    setConsoleError('');
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days} days, ${hours}:${minutes.toString().padStart(2, '0')}`;
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}`;
    return `${minutes} min`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    // Status badge tones in the logo family (navy + yellow + neutral):
    //   running    → solid yellow on navy text  (the "good" steady state)
    //   suspended  → pale yellow tint           (caution but reversible)
    //   stopped    → solid navy on yellow       (powered down but intentional)
    //   creating   → faint yellow + navy        (transitional)
    //   terminated → gray (structural neutral; not a hue)
    switch (status) {
      case 'running':
        return 'bg-secondary-500 text-primary-navy border-secondary-600';
      case 'suspended':
        return 'bg-secondary-100 text-primary-navy border-secondary-300';
      case 'stopped':
        return 'bg-primary-navy text-secondary-300 border-primary-900';
      case 'creating':
        return 'bg-secondary-50 text-primary-navy border-secondary-200';
      case 'terminated':
      case 'failed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <DashShell title="VPS instance" subtitle="Loading…">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 rounded-2xl bg-white/60 backdrop-blur" />
            <div className="h-32 rounded-2xl bg-white/60 backdrop-blur" />
            <div className="h-32 rounded-2xl bg-white/60 backdrop-blur" />
          </div>
          <div className="h-96 rounded-2xl bg-white/60 backdrop-blur" />
        </div>
      </DashShell>
    );
  }

  if (!vps) {
    return (
      <DashShell title="VPS instance" subtitle="Not found">
        <div className="rounded-2xl bg-white/80 backdrop-blur border border-white/40 p-8 text-center shadow-sm max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="mt-2 text-lg font-semibold text-slate-900">VPS not found</h2>
          <p className="mt-1 text-sm text-slate-500">The requested VPS instance could not be found.</p>
          <button
            onClick={() => navigate('/customer/vps')}
            className="mt-6 inline-flex items-center px-4 py-2 bg-secondary-500 text-primary-navy font-medium rounded-lg hover:bg-secondary-600 transition"
          >
            Back to VPS Dashboard
          </button>
        </div>
      </DashShell>
    );
  }

  return (
    <DashShell title="VPS instance" subtitle={vps?.ipv4_address || 'Manage your virtual private server'}>
      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/customer/vps')}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-primary-navy">{vps.name}</h1>
                  {/* Badge shows the *transitioning* label while a power
                      action is in flight so the user gets immediate
                      feedback rather than seeing the old status hang. */}
                  {busyAction && targetStatus ? (
                    <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border bg-secondary-50 text-primary-navy border-secondary-200">
                      <span className="mr-1.5 h-2 w-2 rounded-full bg-secondary-500 animate-pulse" />
                      {busyAction === 'restart'
                        ? 'restarting…'
                        : busyAction === 'stop' ? 'stopping…' : 'starting…'}
                    </span>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(vps.status)}`}>
                      {vps.status}
                    </span>
                  )}
                </div>
                <p className="text-gray-600">{vps.plan_name} • {vps.os_template} • {vps.server_location || 'New York, USA'}</p>
              </div>
            </div>

            <div className="flex space-x-3">
              {/* Buttons are disabled the moment any action is in flight, so
                  customers can't fire stop + start back-to-back and end up
                  fighting the Proxmox task queue. Hidden behind status so
                  Start is only visible when stopped, etc. */}
              {vps.status === 'stopped' && (
                <button
                  onClick={() => handleVPSControl('start')}
                  disabled={!!busyAction}
                  className="px-4 py-2 bg-secondary-500 text-primary-navy font-medium rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                >
                  {busyAction === 'start' ? (
                    <span className="h-5 w-5 mr-2 border-2 border-primary-navy border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {busyAction === 'start' ? 'Starting…' : 'Start'}
                </button>
              )}

              {vps.status === 'running' && (
                <>
                  <button
                    onClick={() => handleVPSControl('restart')}
                    disabled={!!busyAction}
                    className="px-4 py-2 bg-primary-navy text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                  >
                    {busyAction === 'restart' ? (
                      <span className="h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {busyAction === 'restart' ? 'Restarting…' : 'Restart'}
                  </button>
                  <button
                    onClick={() => handleVPSControl('stop')}
                    disabled={!!busyAction}
                    className="px-4 py-2 bg-primary-navy text-secondary-300 border border-secondary-500/60 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                  >
                    {busyAction === 'stop' ? (
                      <span className="h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    )}
                    {busyAction === 'stop' ? 'Stopping…' : 'Stop'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Alerts — error and success both use brand-yellow tints; the
            leading icon carries the severity. No off-brand red/green. */}
        {error && (
          <div className="bg-secondary-100 border border-secondary-500 rounded-lg p-4 mb-6">
            <div className="flex">
              <span className="mr-3 text-primary-navy" aria-hidden>🚨</span>
              <div className="flex-1">
                <p className="text-sm text-primary-navy font-medium">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-primary-navy text-sm underline ml-4">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-secondary-50 border border-secondary-300 rounded-lg p-4 mb-6">
            <div className="flex">
              <span className="mr-3 text-primary-navy" aria-hidden>✓</span>
              <div className="flex-1">
                <p className="text-sm text-primary-navy">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-primary-navy text-sm underline ml-4">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-xl mb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl pointer-events-none"></div>
          
          <div className="relative z-10">
            <div className="px-6 py-4 border-b border-gray-200">
              <nav className="flex space-x-8">
                {[
                  { id: 'overview', name: 'Overview', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> },
                  { id: 'console', name: 'Console', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                  { id: 'networking', name: 'Networking', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg> },
                  { id: 'snapshots', name: 'Snapshots', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
                  { id: 'monitoring', name: 'Monitoring', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> },
                  { id: 'settings', name: 'Settings', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Resource Usage Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 relative overflow-hidden shadow-lg">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-600">CPU Usage</h3>
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          {metrics ? `${metrics.cpu_usage.toFixed(1)}%` : '--'}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(metrics?.cpu_usage || 0, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{vps.cpu_cores} vCPU cores</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 relative overflow-hidden shadow-lg">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-600">Memory Usage</h3>
                          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          {metrics ? `${metrics.memory_usage.toFixed(1)}%` : '--'}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(metrics?.memory_usage || 0, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{Math.round(vps.memory_mb / 1024)} GB RAM</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 relative overflow-hidden shadow-lg">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-600">Disk Usage</h3>
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                            </svg>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          {metrics ? `${metrics.disk_usage.toFixed(1)}%` : '--'}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(metrics?.disk_usage || 0, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{vps.disk_gb} GB SSD</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 relative overflow-hidden shadow-lg">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-600">Uptime</h3>
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          {metrics ? formatUptime(metrics.uptime) : '--'}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          Load: {metrics?.load_average?.map(l => l.toFixed(2)).join(', ') || '--'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trend charts (last hour). Pulled from Proxmox rrddata
                      so they reflect actual VM activity, not synthetic data. */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-3">CPU — last hour</h3>
                      <Sparkline
                        values={series.map(p => (p.cpu || 0) * 100)}
                        color="#3b82f6"
                        unit="%"
                        format={v => v.toFixed(1)}
                      />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-3">Memory — last hour</h3>
                      <Sparkline
                        values={series.map(p => p.maxmem ? ((p.mem || 0) / p.maxmem) * 100 : 0)}
                        color="#10b981"
                        unit="%"
                        format={v => v.toFixed(1)}
                      />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-3">Network in — last hour</h3>
                      <Sparkline
                        values={series.map(p => p.netin || 0)}
                        color="#8b5cf6"
                        format={formatBytes}
                        unit="/s"
                      />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-3">Network out — last hour</h3>
                      <Sparkline
                        values={series.map(p => p.netout || 0)}
                        color="#f59e0b"
                        format={formatBytes}
                        unit="/s"
                      />
                    </div>
                  </div>

                  {/* VPS Information */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow-lg border border-gray-200 p-6 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <svg className="h-5 w-5 mr-2 text-primary-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                          </svg>
                          Instance Details
                        </h3>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Instance ID:</span>
                          <span className="font-mono text-sm">{vps.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Container Name:</span>
                          <span className="font-mono text-sm">{vps.container_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Plan:</span>
                          <span className="font-medium">{vps.plan_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Operating System:</span>
                          <span className="font-medium">{vps.os_template}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Server Location:</span>
                          <span className="font-medium">{vps.server_location || 'New York, USA'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Created:</span>
                          <span className="font-medium">{new Date(vps.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow-lg border border-gray-200 p-6 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                      <div className="relative z-10">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="h-5 w-5 mr-2 text-primary-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        Network Information
                      </h3>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">IPv4 Address:</span>
                          <span className="font-mono text-sm">{vps.ipv4_address || 'Pending...'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">IPv6 Address:</span>
                          <span className="font-mono text-sm">{vps.ipv6_address || 'Not configured'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Network In:</span>
                          <span className="font-medium">{metrics ? `${formatBytes(metrics.network_in)}/s` : '--'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Network Out:</span>
                          <span className="font-medium">{metrics ? `${formatBytes(metrics.network_out)}/s` : '--'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Active Connections:</span>
                          <span className="font-medium">{metrics?.connections ?? '--'}</span>
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Console Tab */}
              {activeTab === 'console' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">VPS Console (noVNC)</h3>
                          {consoleStatus && (
                            <p className="text-xs text-gray-500 mt-1">{consoleStatus}</p>
                          )}
                          {consoleError && (
                            <p className="text-xs text-primary-navy mt-1">{consoleError}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          {!consoleUrl ? (
                            <button
                              onClick={connectConsole}
                              className="px-4 py-2 bg-secondary-500 text-primary-navy font-medium rounded-lg hover:bg-secondary-600 transition-colors"
                            >
                              Connect
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={disconnectConsole}
                                className="px-4 py-2 bg-primary-navy text-white rounded-lg hover:bg-primary-700 transition-colors"
                              >
                                Disconnect
                              </button>
                              <a
                                href={consoleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                              >
                                Open in window
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Inline noVNC viewer. The /vnc/<sessionId> page renders
                        the canvas, status bar + Ctrl+Alt+Del button itself. */}
                    {consoleUrl ? (
                      <iframe
                        title="VPS noVNC console"
                        src={consoleUrl}
                        className="block w-full bg-gray-900 border-0"
                        style={{ height: '70vh' }}
                        allow="clipboard-read; clipboard-write"
                      />
                    ) : (
                      <div className="bg-gray-50 text-gray-500 text-sm p-12 text-center">
                        Click <strong>Connect</strong> to open a browser-based console session.
                        The session expires after 5 minutes of inactivity.
                      </div>
                    )}
                  </div>

                  {/* SSH Access Information */}
                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="h-5 w-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      SSH Access
                    </h4>
                    <p className="text-gray-700 mb-4">
                      For full terminal access, connect via SSH using the following command:
                    </p>
                    <div className="bg-white rounded-lg p-4 font-mono text-sm border border-orange-200">
                      <code>ssh ubuntu@{vps.ipv4_address || '[IP_PENDING]'}</code>
                    </div>
                    <p className="text-gray-600 text-sm mt-3">
                      Default username: <strong>ubuntu</strong> &nbsp;·&nbsp; default password is the one shown at provisioning (change it on first login).<br/>
                      You can also add SSH keys from the Settings tab for passwordless access.
                    </p>
                  </div>
                </div>
              )}

              {/* Other Tabs */}
              {(activeTab === 'networking' || activeTab === 'snapshots' || activeTab === 'monitoring') && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full text-gray-700">
                    <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
                    <span className="text-sm font-medium">Coming Soon</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {activeTab === 'networking' && 'Advanced Networking Features'}
                    {activeTab === 'snapshots' && 'Backup & Snapshot Management'}
                    {activeTab === 'monitoring' && 'Performance Monitoring & Alerts'}
                  </h3>
                  <p className="text-gray-600">
                    This feature is under development and will be available soon.
                  </p>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {/* General Settings */}
                  <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow-lg border border-gray-200 p-6 relative overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">VPS Name</label>
                        <input
                          type="text"
                          defaultValue={vps.name}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Security Settings */}
                  <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow-lg border border-gray-200 p-6 relative overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h3>
                    
                    {/* Root Password */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Root Password</h4>
                        <button
                          onClick={() => setShowPasswordReset(!showPasswordReset)}
                          className="text-orange-600 hover:text-orange-700 text-sm"
                        >
                          {showPasswordReset ? 'Cancel' : 'Reset Password'}
                        </button>
                      </div>
                      
                      {showPasswordReset && (
                        <div className="space-y-3">
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                          <button
                            onClick={() => {
                              setSuccess('Password reset initiated. This may take a few moments.');
                              setShowPasswordReset(false);
                              setNewPassword('');
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg hover:from-orange-600 hover:to-yellow-600 transition-all duration-200"
                          >
                            Update Password
                          </button>
                        </div>
                      )}
                    </div>

                    {/* SSH Keys */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">SSH Keys</h4>
                      <div className="space-y-3">
                        {vps.ssh_keys.length > 0 ? (
                          <div className="space-y-2">
                            {vps.ssh_keys.map((key, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="font-mono text-sm truncate">{key.substring(0, 50)}...</span>
                                <button className="text-primary-navy hover:text-primary-700 text-sm underline">Remove</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No SSH keys configured</p>
                        )}
                        
                        <div className="border-t pt-4">
                          <input
                            type="text"
                            value={sshKeyName}
                            onChange={(e) => setSshKeyName(e.target.value)}
                            placeholder="SSH Key name"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                          <textarea
                            value={newSSHKey}
                            onChange={(e) => setNewSSHKey(e.target.value)}
                            placeholder="Paste your SSH public key here..."
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                          <button
                            onClick={() => {
                              if (newSSHKey.trim()) {
                                setSuccess('SSH key added successfully');
                                setNewSSHKey('');
                                setSshKeyName('');
                              }
                            }}
                            className="px-4 py-2 bg-secondary-500 text-primary-navy font-medium rounded-lg hover:bg-secondary-600 transition-colors"
                          >
                            Add SSH Key
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Danger Zone — irreversible action. Stays in-brand by
                      using a deep yellow tint background with bold navy
                      text + 🚨 icon to signal severity (no off-brand red). */}
                  <div className="bg-secondary-100 border border-secondary-500 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-primary-navy mb-4">🚨 Danger Zone</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-primary-navy">Destroy VPS</h4>
                          <p className="text-primary-navy/80 text-sm">Permanently delete this VPS and all its data. This action cannot be undone.</p>
                        </div>
                        <button
                          onClick={() => handleVPSControl('destroy')}
                          disabled={!!busyAction}
                          className="px-4 py-2 bg-primary-navy text-white border-2 border-secondary-500 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                        >
                          {busyAction === 'destroy' && (
                            <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                          {busyAction === 'destroy' ? 'Destroying…' : 'Destroy VPS'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashShell>
  );
};

export default EnhancedVPSInstanceDetail;